import { Component, EventEmitter, OnInit } from '@angular/core';
import {
  DwButtonCommand,
  DwButtonConfig,
  DwModalConfig,
  DwModalHandler,
  DwModalResult
} from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';

export interface ReportDashboardCreateModalData {
  groupDisplay: string;
  existingDashboardKeys: string[];
  defaultDataSourceItemName?: string;
  simpleMode?: boolean;
  mode?: 'create' | 'clone';
  sourceDashboardDisplay?: string;
  initialValues?: Partial<ReportDashboardCreateModalResult>;
}

export interface ReportDashboardCreateModalResult {
  dashboardKey: string;
  dashboardDisplay: string;
  tabDisplay: string;
  title: string;
  description?: string;
  iconClass?: string;
  dataSourceItemName?: string;
  isActive: boolean;
}

@Component({
  selector: 'app-report-dashboard-create-modal',
  templateUrl: './report-dashboard-create-modal.component.html',
  styleUrls: ['./report-dashboard-create-modal.component.scss']
})
export class ReportDashboardCreateModalComponent implements OnInit, DwModalHandler {
  modalConfig: DwModalConfig<ReportDashboardCreateModalData>;
  closeModal = new EventEmitter<DwModalResult<ReportDashboardCreateModalResult>>();

  model: ReportDashboardCreateModalResult = {
    dashboardKey: '',
    dashboardDisplay: '',
    tabDisplay: '',
    title: '',
    description: '',
    iconClass: 'fa fa-bar-chart',
    dataSourceItemName: 'roCheckrideFactBase',
    isActive: true
  };
  errorMessage: string;
  private keyEdited = false;
  private displayEdited = false;
  private titleEdited = false;

  get simpleMode(): boolean {
    return !!this.modalConfig.data?.simpleMode;
  }

  get cloneMode(): boolean {
    return this.modalConfig.data?.mode === 'clone';
  }

  ngOnInit(): void {
    const initialValues = this.modalConfig.data?.initialValues;
    this.model = {
      ...this.model,
      ...(initialValues || {}),
      dataSourceItemName: initialValues?.dataSourceItemName
        || this.modalConfig.data?.defaultDataSourceItemName
        || 'roCheckrideFactBase'
    };
    if (initialValues) {
      this.keyEdited = !!initialValues.dashboardKey;
      this.displayEdited = !!initialValues.dashboardDisplay;
      this.titleEdited = !!initialValues.title;
    }
  }

  onTabDisplayChanged(value: string): void {
    if (!this.displayEdited) {
      this.model.dashboardDisplay = value;
    }
    if (!this.keyEdited) {
      this.model.dashboardKey = this.toKey(value);
    }
    if (!this.titleEdited) {
      this.model.title = value;
    }
  }

  markKeyEdited(): void {
    this.keyEdited = true;
  }

  markDisplayEdited(): void {
    this.displayEdited = true;
  }

  markTitleEdited(): void {
    this.titleEdited = true;
  }

  buttonClick(buttonConfig: DwButtonConfig): Observable<DwModalResult<ReportDashboardCreateModalResult>> {
    if (buttonConfig.command !== DwButtonCommand.saveAndClose) {
      return of({
        cancelled: true,
        closeModal: true,
        buttonCommand: buttonConfig.command
      } as DwModalResult<ReportDashboardCreateModalResult>);
    }

    const validationError = this.validate();
    if (validationError) {
      this.errorMessage = validationError;
      return of({ closeModal: false } as DwModalResult<ReportDashboardCreateModalResult>);
    }

    this.errorMessage = null;
    return of({
      cancelled: false,
      closeModal: true,
      buttonCommand: buttonConfig.command,
      data: {
        ...this.model,
        dashboardKey: this.model.dashboardKey.trim(),
        dashboardDisplay: this.model.dashboardDisplay.trim(),
        tabDisplay: this.model.tabDisplay.trim(),
        title: this.model.title.trim()
      }
    } as DwModalResult<ReportDashboardCreateModalResult>);
  }

  private validate(): string {
    if (!this.model.tabDisplay?.trim() || !this.model.dashboardDisplay?.trim()
      || !this.model.dashboardKey?.trim() || !this.model.title?.trim()) {
      return this.simpleMode
        ? 'Dashboard name is required.'
        : 'Tab name, dashboard key, dashboard display, and page title are required.';
    }

    if (!/^[a-z0-9][a-z0-9-]*$/.test(this.model.dashboardKey.trim())) {
      return 'Dashboard key must use lowercase letters, numbers, and hyphens.';
    }

    const existingKeys = (this.modalConfig.data?.existingDashboardKeys || [])
      .map(key => key.toLowerCase());
    if (existingKeys.indexOf(this.model.dashboardKey.trim().toLowerCase()) >= 0) {
      return `Dashboard key ${this.model.dashboardKey.trim()} is already in use.`;
    }

    return null;
  }

  private toKey(value: string): string {
    return (value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}
